import type { IntlShape } from 'react-intl';

import { SVGIcons } from '@/components/atoms/Icon/SVGIcon';
import type { TableRow } from '@/components/atoms/Table/Table.type';
import { computeText } from '@/locales/utils';
import { theme } from '@/core/theme';
import type { MenuCategory } from '@/services/innovorder/categories/categories.type';
import type { MenuProduct } from '@/services/innovorder/products/products.type';
import type { PromotionalBanner } from '@/services/innovorder/promotionalBanner/promotionalBanner.type';

import { buildBannerActionCell } from '@/pages/PromotionalBanners/utils/buildBannerActionCell';
import {
    buildBannerStatusBadge,
    buildBannerLinkTypeCell,
} from '@/pages/PromotionalBanners/utils/buildBannerStatusBadge';
import { getLinkTypeInfo } from './getLinkTypeInfo';

type BuildRowsParams = {
    banners: PromotionalBanner[];
    intl: IntlShape;
    products: MenuProduct[];
    categories: MenuCategory[];
    onToggle: (banner: PromotionalBanner) => void;
    onEdit: (banner: PromotionalBanner) => void;
    onView: (banner: PromotionalBanner) => void;
    onDelete: (banner: PromotionalBanner) => void;
    onDuplicate?: (banner: PromotionalBanner) => void;
};

export const buildPromotionalBannerRows = ({
    banners,
    intl,
    products,
    categories,
    onToggle,
    onEdit,
    onView,
    onDelete,
    onDuplicate,
}: BuildRowsParams): TableRow[] =>
    banners.map((banner): TableRow => {
        const isRestaurantBanner = banner.brandId === null;
        const linkInfo = getLinkTypeInfo(banner, intl, products, categories);

        return {
            id: { type: 'identifier' as const, value: banner.promotionalBannerId },
            name: {
                type: 'string' as const,
                value: { text: banner.name },
            },
            status: {
                type: 'badge' as const,
                value: buildBannerStatusBadge(banner.isActive, intl),
            },
            source: {
                type: 'badge' as const,
                value: {
                    icon: isRestaurantBanner ? SVGIcons.RESTAURANT : SVGIcons.BRAND_V2,
                    text: computeText(
                        intl,
                        isRestaurantBanner
                            ? 'promotionalBanner.table.sourceRestaurant'
                            : 'promotionalBanner.table.sourceBrand',
                    ),
                    textColor: theme.savory.colors.neutral[900],
                    backgroundColor: theme.savory.colors.neutral[100],
                },
            },
            linkType: buildBannerLinkTypeCell(linkInfo),
            actions: buildBannerActionCell({
                isActive: banner.isActive,
                intl,
                onToggle: () => onToggle(banner),
                dropdownItems: [
                    ...(isRestaurantBanner
                        ? [
                              {
                                  value: 1,
                                  label: computeText(intl, 'promotionalBanner.action.edit'),
                                  onClick: () => onEdit(banner),
                              },
                          ]
                        : [
                              {
                                  value: 4,
                                  label: computeText(intl, 'promotionalBanner.action.view'),
                                  onClick: () => onView(banner),
                              },
                          ]),
                    ...(onDuplicate
                        ? [
                              {
                                  value: 3,
                                  label: computeText(intl, 'promotionalBanner.action.duplicate'),
                                  onClick: () => onDuplicate(banner),
                              },
                          ]
                        : []),
                    ...(isRestaurantBanner
                        ? [
                              {
                                  value: 2,
                                  label: computeText(intl, 'promotionalBanner.action.delete'),
                                  onClick: () => onDelete(banner),
                              },
                          ]
                        : []),
                ],
            }),
        };
    });
